import { BoxTruckMailingService } from '@numeo/types-main-web-app'
import { toE164Nanp } from '@numeo/types-core'
import { BoxTruckVerification } from '../services/boxTruckLoadsApi'

/**
 * Which drivers a message can actually reach, on either service — one rule, used by every surface that
 * picks recipients.
 *
 * It lives here rather than next to a table because three places need the same answer and they must not
 * drift: the Mailing picker greys a row out with it, the SMS composer's "select all" skips rows with it,
 * and the server refuses at send time by the same test. A second copy that said "has a phone" where the
 * sender says "has a NANP number" would offer drivers the send then silently skips.
 */

/**
 * Can this driver be texted at all?
 *
 * `toE164Nanp` and not a truthy check on the field: `(111) 222-3344` and `12312312323` are stored on real
 * profiles and look like phone numbers without being dialable, so a truthy test offers them, the operator
 * selects them, and the send reports "3 without a phone" about drivers whose profile plainly shows one.
 */
export const hasTextablePhone = (d: BoxTruckVerification): boolean => !!toE164Nanp(d.boxTruckInfo.info?.phone)

/** Is there a connected claim-record group to post into? */
export const hasTelegram = (d: BoxTruckVerification): boolean => !!d.telegramGroup

export const isMailingReachable = (d: BoxTruckVerification, service: BoxTruckMailingService): boolean =>
    service === 'sms' ? hasTextablePhone(d) : hasTelegram(d)

export const otherMailingService = (service: BoxTruckMailingService): BoxTruckMailingService => (service === 'sms' ? 'telegram' : 'sms')

/**
 * Reachable HERE and nowhere else — the drivers this service is the only way to reach.
 *
 * This is the question an operator actually has when they pick SMS: Telegram costs nothing and goes to
 * everyone who has it, so the message that matters on SMS is the one for the drivers Telegram cannot
 * carry — the app-install instructions, the "connect your group" nudge. Selecting them by hand meant
 * reading two columns down a list of eight hundred.
 */
export const isReachableOnlyOn = (d: BoxTruckVerification, service: BoxTruckMailingService): boolean =>
    isMailingReachable(d, service) && !isMailingReachable(d, otherMailingService(service))

/** One wording for the gap preset, so the button, its empty state and the Mailing and SMS surfaces all
 *  name the same set of drivers the same way. */
export const gapPresetLabel = (service: BoxTruckMailingService): string => (service === 'sms' ? 'without Telegram' : 'without a textable phone')
